import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
/** Roles and permissions (docs/specs/identity.md Part 2). Lists follow `{ data, nextCursor }` (CLAUDE.md §9). */
const permissionKey = z.string().regex(/^[a-z][a-z_]*(\.[a-z][a-z_]*)+$/);
/** `GET /v1/permissions`: every permission the system knows, with its labels. */
export const permissionSchema = z.object({
    key: permissionKey,
    label: z.object({ en: z.string(), bn: z.string() }),
});
export const permissionListSchema = z.object({ data: z.array(permissionSchema), nextCursor: z.null() });
export const roleSchema = z.object({
    id: z.uuid(),
    /** System role key (`owner`, `cashier`, …); null for custom roles. UIs label system roles by key. */
    key: z.string().nullable(),
    name: z.string(),
    isSystem: z.boolean(),
    permissions: z.array(permissionKey),
    version: z.number().int(),
});
/** Roles per tenant are few, so the list is complete (`nextCursor` always null). */
export const roleListSchema = z.object({ data: z.array(roleSchema), nextCursor: z.null() });
const roleName = z.string().trim().min(1).max(100);
const permissionKeys = z.array(permissionKey).max(500);
/** `POST /v1/roles`: a custom role. */
export const createRoleRequestSchema = z.object({ name: roleName, permissions: permissionKeys });
/** `PATCH /v1/roles/{id}`: custom roles only; `version` from the last read (409 if it changed). */
export const updateRoleRequestSchema = z.object({
    name: roleName.optional(),
    permissions: permissionKeys.optional(),
    version: z.number().int().min(1),
});
export const roleAssignmentSchema = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    roleId: z.uuid(),
    /** Null: every location. */
    locationId: z.uuid().nullable(),
});
export const roleAssignmentListSchema = z.object({
    data: z.array(roleAssignmentSchema),
    nextCursor: z.uuid().nullable(),
});
/** `GET /v1/role-assignments?userId=&limit=&cursor=` */
export const roleAssignmentQuerySchema = z.object({
    userId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
/** `POST /v1/role-assignments`: give a member a role, everywhere (`locationId` null/absent) or at one location. */
export const createRoleAssignmentRequestSchema = z.object({
    userId: z.uuid(),
    roleId: z.uuid(),
    locationId: z.uuid().nullable().default(null),
});
// Endpoints -------------------------------------------------------------------------------------------------
export const listPermissions = defineEndpoint({
    name: "listPermissions",
    method: "GET",
    path: "/v1/permissions",
    access: { permission: "roles.view" },
    summary: "Every permission with its labels",
    tag: "roles",
    status: 200,
    response: permissionListSchema,
});
export const listRoles = defineEndpoint({
    name: "listRoles",
    method: "GET",
    path: "/v1/roles",
    access: { permission: "roles.view" },
    summary: "System and custom roles with their permissions",
    tag: "roles",
    status: 200,
    response: roleListSchema,
});
export const getRole = defineEndpoint({
    name: "getRole",
    method: "GET",
    path: "/v1/roles/{id}",
    access: { permission: "roles.view" },
    summary: "One role",
    tag: "roles",
    params: idParamsSchema,
    status: 200,
    response: roleSchema,
});
export const createRole = defineEndpoint({
    name: "createRole",
    method: "POST",
    path: "/v1/roles",
    access: { permission: "roles.manage" },
    summary: "Create a custom role (only with permissions you hold everywhere)",
    tag: "roles",
    body: createRoleRequestSchema,
    status: 201,
    response: roleSchema,
    errors: ["CONFLICT", "FORBIDDEN", "FEATURE_NOT_IN_PLAN"],
    entitlement: "feature.custom_roles",
});
export const updateRole = defineEndpoint({
    name: "updateRole",
    method: "PATCH",
    path: "/v1/roles/{id}",
    access: { permission: "roles.manage" },
    summary: "Change a custom role (optimistic version)",
    tag: "roles",
    params: idParamsSchema,
    body: updateRoleRequestSchema,
    status: 200,
    response: roleSchema,
    errors: ["CONFLICT", "FORBIDDEN", "SYSTEM_ROLE_READ_ONLY"],
});
export const deleteRole = defineEndpoint({
    name: "deleteRole",
    method: "DELETE",
    path: "/v1/roles/{id}",
    access: { permission: "roles.manage" },
    summary: "Delete a custom role that nobody has",
    tag: "roles",
    params: idParamsSchema,
    status: 204,
    errors: ["CONFLICT", "SYSTEM_ROLE_READ_ONLY"],
});
export const listRoleAssignments = defineEndpoint({
    name: "listRoleAssignments",
    method: "GET",
    path: "/v1/role-assignments",
    access: { permission: "roles.view" },
    summary: "Who has which role where",
    tag: "roles",
    query: roleAssignmentQuerySchema,
    status: 200,
    response: roleAssignmentListSchema,
});
export const assignRole = defineEndpoint({
    name: "assignRole",
    method: "POST",
    path: "/v1/role-assignments",
    access: { permission: "roles.assign" },
    summary: "Give a member a role, everywhere or at one location",
    tag: "roles",
    body: createRoleAssignmentRequestSchema,
    status: 201,
    response: roleAssignmentSchema,
    errors: ["CONFLICT", "FORBIDDEN", "PLAN_LIMIT_REACHED"],
    entitlement: "users.max",
});
export const unassignRole = defineEndpoint({
    name: "unassignRole",
    method: "DELETE",
    path: "/v1/role-assignments/{id}",
    access: { permission: "roles.assign" },
    summary: "Take a role away (never the last Owner)",
    tag: "roles",
    params: idParamsSchema,
    status: 204,
    errors: ["FORBIDDEN", "LAST_OWNER"],
});
//# sourceMappingURL=rbac.js.map